<script lang="ts" setup>
import { computed } from 'vue';
import { BAlert, BButton, BModal, BSpinner } from 'bootstrap-vue-next';
import { formatDateTime } from '@/utils/date-utils';
import { InviteType } from '@/gql/graphql';
import { useUserStore } from '@/store/user';

const userStore = useUserStore();

// --- Props for the component ---
interface Props {
  invite: InviteType;
  show: boolean;
}
const props = defineProps<Props>();
const emits = defineEmits(['success', 'close']);

// --- Computed Properties ---
const show = computed<boolean>(() => {
  return props.show;
});

function handleCloseModal() {
  emits('close');
}
</script>
<template>
  <b-modal
    v-if="props.invite.inviter && props.invite.organization"
    v-model="show"
    @hide="handleCloseModal"
  >
    <!-- Title -->
    <template #title>
      <font-awesome-icon :icon="['fas', 'user-plus']" class="me-2" />
      <strong>Invite Details</strong>
    </template>
    <!-- Content -->
    <div>
      <p><strong>Status: </strong>{{ props.invite.status }}</p>
      <p>
        <strong>Organization: </strong>{{ props.invite.organization.name
        }}<br />
        <strong>Invite recipient: </strong>
        <span v-if="props.invite.invited">
          {{ props.invite.invited.givenName }}
          {{ props.invite.invited.familyName }} ({{
            props.invite.invited.email
          }})</span
        >
        <span v-else>{{ props.invite.invitedEmail }}</span>
        <br />
        <strong>User role: </strong>{{ props.invite.invitedOrganizationUserRole
        }}<br />
        <strong>Invited by: </strong>{{ props.invite.inviter.givenName }}
        {{ props.invite.inviter.familyName }} ({{
          props.invite.inviter.email
        }})<br />
        <strong>Invited at: </strong>{{ formatDateTime(props.invite.createdAt)
        }}<br />
        <strong>Last notified: </strong
        >{{ formatDateTime(props.invite.lastNotificationAt) }}<br />
        <strong>Expires at: </strong
        >{{ formatDateTime(props.invite.expiresAt) }}
      </p>
      <template
        v-if="userStore.isAdmin || userStore.id === props.invite.inviter.id"
      >
        <p v-if="props.invite.description">
          <strong>Description: </strong>{{ props.invite.description }}<br />
        </p>
      </template>
      <p v-if="props.invite.message">
        <strong>Message: </strong>{{ props.invite.message }}<br />
      </p>
    </div>

    <!-- Footer with action buttons -->
    <template #footer>
      <BButton variant="secondary" @click="handleCloseModal" class="me-2">
        <span>Close</span>
      </BButton>
    </template>
  </b-modal>
</template>
